import { postRequestForRefreshTemplates } from "../../../../../api/postCall/refreshNotes";
import {
  DiagnosisChangeContext,
  updatePrimaryDiagnosisCode,
} from "../../../../../api/postCall/updatePrimaryDiagnosisCode";
import { singleOrderDetailsGenericUpdateForPostgres } from "../../../../../api/postgreSQL/postCalls/singleOrderDetailsGenericUpdateForPostgres";
import {
  GeneralOrderDetails,
  PrimaryDiagnosisDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { EventsName } from "../../../../../enums/eventsName";
import { SingleOrderDetailsTableName } from "../../../../../enums/singleOrderDetailsTableName";
import { logError } from "../../../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";
import { TableData } from "../../types";
import { ClinicalDataAnalyticsContext } from "../buildClinicalDataAnalytics";
import { computeDiagnosisOutcome, hasDxChanged } from "./diagnosisOutcome";
import {
  buildDxCodeChangeSubmittedAdditionalData,
  buildPrimaryDiagnosisValidatedAdditionalData,
  DiagnosisValidatedAnalyticsInput,
} from "./diagnosisValidatedAnalytics";
import {
  HandleSaveSelectionContext,
  resolvePrimaryDiagnosisForSave,
} from "./resolveDiagnosis";

/** Lifecycle callbacks invoked during the primary Dx save flow. */
export interface HandleSaveOptions {
  onSave?: () => void;
  onStart?: () => void;
  onFinish?: () => void;
  onError?: (error: any) => void;
}

/**
 * Emits validation and override analytics after a successful primary Dx save.
 *
 * @param input - Shared analytics input built from the save context.
 * @param orderId - Medical PA order ID.
 * @param user - Acting user metadata for analytics.
 * @param isValidationRequired - When true, emits `primary_diagnosis_validated`.
 */
const emitPrimaryDiagnosisAnalytics = (
  input: DiagnosisValidatedAnalyticsInput,
  orderId: string,
  user?: { id?: string; email?: string | null },
  isValidationRequired: boolean = false,
): void => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const patientId =
    input.generalOrderDetails?.demographics?.patient_id ??
    input.generalOrderDetails?.demographics?.patient_mrn ??
    input.analyticsContext?.patientId ??
    "";
  const baseEvent = {
    patient_id: patientId,
    org_id: orgId || input.analyticsContext?.orgId || "",
    order_id: orderId,
    user_id: user?.id ?? input.analyticsContext?.userId ?? "",
    user_email: user?.email ?? input.analyticsContext?.userEmail ?? "",
  };

  if (isValidationRequired) {
    apiWorkerManager.enqueueAndProcess({
      ...baseEvent,
      event_name: EventsName.PRIMARY_DIAGNOSIS_VALIDATED,
      additional_data: buildPrimaryDiagnosisValidatedAdditionalData(input),
    });
  }

  if (hasDxChanged(input.diagnosisOutcome)) {
    apiWorkerManager.enqueueAndProcess({
      ...baseEvent,
      event_name: EventsName.DX_CODE_CHANGE_SUBMITTED,
      additional_data: buildDxCodeChangeSubmittedAdditionalData(input),
    });
  }
};

/**
 * Persists the selected primary Dx and refreshes order context data. When
 * primary Dx validation is required for the order, it additionally marks the
 * order as validated in Postgres and logs the validation analytics event.
 *
 * @param primaryDiagnosisDetails - Existing submission diagnosis entries.
 * @param selectedPrimaryCode - Dropdown value chosen by the user.
 * @param tableData - Patient Dx rows shown in the modal table.
 * @param signal - Abort signal for template refresh.
 * @param orderId - Medical PA order ID.
 * @param options - Optional save lifecycle callbacks.
 * @param fetchAll - Optional callback to reload order context data.
 * @param selectionContext - Manual-entry values when applicable.
 * @param user - Acting user metadata for analytics.
 * @param generalOrderDetails - Order demographics for analytics.
 * @param defaultPrimaryCode - Stack default ICD code before user action.
 * @param dropDownOptions - Dropdown options used for outcome classification.
 * @param screenName - Analytics screen name override.
 * @param isValidationRequired - When true, marks the order's primary Dx as
 *   validated in Postgres and emits the validation analytics event; otherwise
 *   these side effects are skipped. Defaults to `false`.
 * @param changeContext - Optional user-provided reason/source captured when the
 *   primary Dx is changed; persisted alongside the diagnosis entry.
 * @param analyticsContext - Shared modal analytics context (payer, auth-on-file).
 */
export const handleSave = async (
  primaryDiagnosisDetails: PrimaryDiagnosisDetails[],
  selectedPrimaryCode: string,
  tableData: TableData[],
  signal: AbortSignal,
  orderId?: string,
  options?: HandleSaveOptions,
  fetchAll?: (orderId: string, orgId: string) => Promise<void>,
  selectionContext?: HandleSaveSelectionContext,
  user?: { id?: string; email?: string | null },
  generalOrderDetails?: GeneralOrderDetails,
  defaultPrimaryCode?: string,
  dropDownOptions?: { value: string; label: string }[],
  screenName?: string,
  isValidationRequired: boolean = false,
  changeContext?: DiagnosisChangeContext,
  analyticsContext?: ClinicalDataAnalyticsContext,
  sibling_order_ids: string[] = [],
) => {
  if (!orderId || !selectedPrimaryCode) return;

  const resolvedDiagnosis = resolvePrimaryDiagnosisForSave(
    selectedPrimaryCode,
    tableData,
    selectionContext,
  );
  if (!resolvedDiagnosis) return;

  const { icdCode, icdDescription } = resolvedDiagnosis;

  options?.onStart?.();

  try {
    const diagnosisOutcome = computeDiagnosisOutcome({
      icdCode,
      defaultPrimaryCode,
      dropDownOptions,
    });

    await updatePrimaryDiagnosisCode(
      orderId,
      primaryDiagnosisDetails ?? [],
      icdCode,
      icdDescription,
      true,
      changeContext,
      sibling_order_ids,
    );

    if (isValidationRequired) {
      await singleOrderDetailsGenericUpdateForPostgres({
        order_id: orderId,
        org_id: getOrgIdForFetchExternalWorklist(),
        table_name: SingleOrderDetailsTableName.ORDER_STATUS,
        data: {
          is_primary_diagnosis_validated: true,
        },
      });
    }

    emitPrimaryDiagnosisAnalytics(
      {
        diagnosisOutcome,
        generalOrderDetails,
        analyticsContext,
        primaryDiagnosisDetails,
        tableData,
        changeContext,
        screenName,
      },
      orderId,
      user,
      isValidationRequired,
    );

    await postRequestForRefreshTemplates(
      orderId ?? "",
      sibling_order_ids ?? [],
      signal,
    );
    if (fetchAll) {
      const orgId = getOrgIdForFetchExternalWorklist();
      if (orderId && orgId) {
        await fetchAll(orderId, orgId);
      }
    }

    options?.onSave?.();
  } catch (error) {
    logError(error as Error, "Error saving primary diagnosis");
    options?.onError?.(error);
  } finally {
    options?.onFinish?.();
  }
};
